import { useMemo } from 'react';
import { useModuleConfig } from './queries/admin/useModuleConfig';
import type { IemModuleConfig } from '../types/moduleConfig';

/** Shape of the person-label bag shared with non-React code (runners/templates). */
export interface PersonLabels {
  /** Title case singular, e.g. "Individual" or "Service Member" */
  label: string;
  /** Title case plural, e.g. "Individuals" or "Service Members" */
  labelPlural: string;
  /** Lowercase singular, e.g. "individual" or "service member" */
  lower: string;
  /** Lowercase plural, e.g. "individuals" or "service members" */
  lowerPlural: string;
  /** Indefinite article for lowercase singular: "an" before vowels, "a" otherwise */
  article: 'a' | 'an';
  /** Configurable label for the individual's ID field, e.g. "Individual ID" */
  individualIdLabel: string;
  /** Prefix for entity identifiers, e.g. "INDV-" */
  individualIdPrefix: string;
}

/** Return "a" or "an" based on whether `word` starts with a vowel sound. */
const indefiniteArticle = (word: string): 'a' | 'an' =>
  /^[aeiou]/i.test(word) ? 'an' : 'a';

const FALLBACK: PersonLabels = {
  label: 'Individual',
  labelPlural: 'Individuals',
  lower: 'individual',
  lowerPlural: 'individuals',
  article: 'an',
  individualIdLabel: 'Individual ID',
  individualIdPrefix: 'INDV-',
};

/** Extract PersonLabels from a report-runner params bag (key: `personLabels`). */
export function getPersonLabels(params: Record<string, unknown>): PersonLabels {
  return (params.personLabels as PersonLabels | undefined) ?? FALLBACK;
}

/** Screens that can have independent person label overrides. */
export type PersonScreen = 'front_window' | 'reports' | 'global_search';

/**
 * Provides the configurable person label (e.g. "Individual" / "Service Member")
 * from the IEM module configuration. All variants are derived from the
 * title-case config values so callers don't need to manipulate casing.
 *
 * Pass a screen name to get the per-screen override; falls back to the
 * global person_label / person_label_plural if the per-screen value is empty.
 */
export function usePersonLabels(screen?: PersonScreen) {
  const { config } = useModuleConfig<IemModuleConfig>('iem');
  const identity = config.features.identity;

  return useMemo(() => {
    const globalSingular = identity.person_label;
    const globalPlural = identity.person_label_plural;

    // Resolve per-screen overrides (fall back to global)
    let singular = globalSingular;
    let plural = globalPlural;
    if (screen === 'front_window') {
      singular = identity.front_window_person_label || globalSingular;
      plural = identity.front_window_person_label_plural || globalPlural;
    } else if (screen === 'reports') {
      singular = identity.reports_person_label || globalSingular;
      plural = identity.reports_person_label_plural || globalPlural;
    } else if (screen === 'global_search') {
      singular = identity.global_search_person_label || globalSingular;
      plural = identity.global_search_person_label_plural || globalPlural;
    }

    const rawPrefix = identity.individual_id_prefix;
    return {
      label: singular,
      labelPlural: plural,
      lower: singular.toLowerCase(),
      lowerPlural: plural.toLowerCase(),
      article: indefiniteArticle(singular),
      individualIdLabel: identity.individual_id_label,
      individualIdPrefix: rawPrefix.endsWith('-') ? rawPrefix : `${rawPrefix}-`,
    };
  }, [identity, screen]);
}
